
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import AGENT_TEMPLATES from '@/data/agentTemplates';
import CategoryTabs from '@/components/dashboard/agent-builder/CategoryTabs';
import TemplateSelector from '@/components/dashboard/agent-builder/TemplateSelector';
import DocumentUploader from '@/components/dashboard/agent-builder/DocumentUploader';
import { useToast } from '@/hooks/use-toast';

interface IndustryTemplateStepProps {
  agentData: any;
  setAgentData: (data: any) => void;
  userId: string;
}

const IndustryTemplateStep: React.FC<IndustryTemplateStepProps> = ({ agentData, setAgentData, userId }) => {
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<string>(agentData.industry || 'general');
  const [sopDocument, setSopDocument] = useState<File[]>(agentData.sopDocument ? [agentData.sopDocument] : []);

  const filteredTemplates = AGENT_TEMPLATES.filter(
    template => template.category === selectedCategory || 
                (selectedCategory === 'all' && template.category !== 'custom')
  );

  const handleTemplateChange = (templateId: string) => {
    const template = AGENT_TEMPLATES.find(t => t.id === templateId);
    
    if (template) {
      const defaults = template.settings.reduce((acc, setting) => {
        acc[setting.id] = setting.default;
        return acc;
      }, {} as Record<string, string>);
      
      setAgentData({
        ...agentData,
        template: templateId,
        templateSettings: defaults
      });
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setAgentData({
      ...agentData,
      industry: category
    });
  };

  const handleDocumentUpload = (files: File[]) => {
    if (files.length > 0) {
      setSopDocument(files);
      setAgentData({
        ...agentData,
        sopDocument: files[0]
      });

      toast({
        title: "Document uploaded",
        description: `${files[0].name} will be analyzed to extract SOP functions`
      });
    }
  };

  const selectedTemplateFull = AGENT_TEMPLATES.find(t => t.id === agentData.template);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Industry / Category</Label>
        <CategoryTabs 
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
        />
      </div>

      <div className="space-y-2">
        <TemplateSelector
          selectedTemplate={agentData.template}
          onTemplateChange={handleTemplateChange}
          filteredTemplates={filteredTemplates}
          selectedCategory={selectedCategory}
          selectedTemplateFull={selectedTemplateFull}
        />
      </div>

      <div className="space-y-2">
        <Label>SOP Document</Label>
        <p className="text-sm text-gray-600 mb-2">
          Upload your Standard Operating Procedure document. This will be analyzed to extract logical functions.
        </p>
        <DocumentUploader
          uploadedDocuments={sopDocument}
          setUploadedDocuments={handleDocumentUpload}
        />
      </div>

      {agentData.sopDocument && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-green-800 text-sm">
              <strong>Document uploaded:</strong> {agentData.sopDocument.name}
            </p>
            <p className="text-green-700 text-xs mt-1">
              This document will be used to extract SOP logical functions
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IndustryTemplateStep;
